import React, { useCallback, useEffect, useState } from 'react';
import AdminPage from '../../components/admin/AdminPage';
import useAuth from '../../hooks/useAuth';
import useCallApiAndLoad from '../../hooks/useCallApiAndLoad';
import AdminPaginator from '../../components/admin/AdminPaginator';
import {
  PaymentDashboardItem,
  PaymentDashboardKpis,
  ReferralItem,
  ReferralKpis,
  ReferralPaginationMeta,
} from './models/referralModel';
import {
  getAdminPaymentListEndpoint,
  getAdminReferralListEndpoint,
  getResponsiblesListEndpoint,
  markReferralPaidEndpoint,
  registerReferralProspectEndpoint,
  sendReferralReminderEndpoint,
} from './services/referralService';
import ReferralKpiCards from './components/referrals/ReferralKpiCards';
import ReferralFilterBar from './components/referrals/ReferralFilterBar';
import ReferralListTable from './components/referrals/ReferralListTable';
import PaymentKpiCards from './components/referrals/PaymentKpiCards';
import PaymentFilterBar from './components/referrals/PaymentFilterBar';
import PaymentListTable from './components/referrals/PaymentListTable';
import MarkPaidModal from './components/referrals/MarkPaidModal';
import { Option } from '../../models/formModel';
import { fetchResponseAdapter } from '../../adapters/fetchAdapter';
import { listToSelectOptionAdapter } from '../../adapters/listAdapter';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ErrorAlert, SuccessAlert } from '../../models/alertModel';

import useUserCountry from '../../hooks/useUserCountry';

const DEFAULT_KPIS: ReferralKpis = {
  total: 0,
  in_gestion: 0,
  without_contact: 0,
  successful: 0,
  not_apply: 0,
};

const DEFAULT_PAYMENT_KPIS: PaymentDashboardKpis = {
  to_pay_total: 0,
  paid_total: 0,
  ready_to_pay_count: 0,
  incomplete_data_count: 0,
};

const DEFAULT_META: ReferralPaginationMeta = {
  page: 1,
  take: 10,
  itemCount: 0,
  pageCount: 1,
  hasPreviousPage: false,
  hasNextPage: false,
};

const InvestorReferralPlans: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const { isLoading, callEndpoint } = useCallApiAndLoad();
  const { getCurrentCountry } = useUserCountry();
  const currentCountryCode = getCurrentCountry()?.code?.toLowerCase() || 'sv';

  // Estados generales: leer si viene tab=payments en la URL
  const initialTab = searchParams.get('tab') === 'payments' ? 'payments' : 'referrals';
  const [activeTab, setActiveTab] = useState<'referrals' | 'payments'>(initialTab);
  const [alertOptions, setAlertOptions] = useState<SuccessAlert | ErrorAlert | null>(null);

  // Sincronizar si cambia el parámetro de URL
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'payments' || tabParam === 'referrals') {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  const handleTabChange = (tab: 'referrals' | 'payments') => {
    setActiveTab(tab);
    setSearchParams(tab === 'payments' ? { tab: 'payments' } : {});
  };

  // Estados de la pestaña Referidos
  const [items, setItems] = useState<ReferralItem[]>([]);
  const [kpis, setKpis] = useState<ReferralKpis>(DEFAULT_KPIS);
  const [meta, setMeta] = useState<ReferralPaginationMeta>(DEFAULT_META);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [search, setSearch] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedLine, setSelectedLine] = useState<string>('');
  const [selectedResponsibleId, setSelectedResponsibleId] = useState<string>('');
  const [responsibleOptions, setResponsibleOptions] = useState<Option[]>([]);
  const [isLoadingResponsibles, setIsLoadingResponsibles] = useState<boolean>(true);
  const [registeringProspectId, setRegisteringProspectId] = useState<string | null>(null);

  // Estados de la pestaña Dashboard Pagos
  const [paymentItems, setPaymentItems] = useState<PaymentDashboardItem[]>([]);
  const [paymentKpis, setPaymentKpis] = useState<PaymentDashboardKpis>(DEFAULT_PAYMENT_KPIS);
  const [paymentMeta, setPaymentMeta] = useState<ReferralPaginationMeta>(DEFAULT_META);
  const [paymentCurrentPage, setPaymentCurrentPage] = useState<number>(1);
  const [paymentSearch, setPaymentSearch] = useState<string>('');
  const [paymentStartDate, setPaymentStartDate] = useState<string>('');
  const [paymentEndDate, setPaymentEndDate] = useState<string>('');
  const [paymentSelectedStatus, setPaymentSelectedStatus] = useState<string>('');
  const [paymentSelectedCategory, setPaymentSelectedCategory] = useState<string>('');
  const [selectedPaymentIds, setSelectedPaymentIds] = useState<string[]>([]);
  const [isSendingReminderId, setIsSendingReminderId] = useState<string | null>(null);
  const [isSendingBatchReminders, setIsSendingBatchReminders] = useState<boolean>(false);

  // Modal para marcar como pagado
  const [markPaidItem, setMarkPaidItem] = useState<PaymentDashboardItem | null>(null);
  const [isMarkPaidModalOpen, setIsMarkPaidModalOpen] = useState(false);
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);

  // Carga de la lista de responsables
  const loadResponsibles = useCallback(async () => {
    try {
      setIsLoadingResponsibles(true);
      const { status, data } = await callEndpoint(
        getResponsiblesListEndpoint(token, currentCountryCode),
      );
      if (status === 200) {
        const response = fetchResponseAdapter(data);
        const options = listToSelectOptionAdapter(response.data || [], 'id', 'name', false);
        setResponsibleOptions(options);
      }
    } catch (error) {
      console.error('Error loading responsibles:', error);
    } finally {
      setIsLoadingResponsibles(false);
    }
  }, [callEndpoint, token, currentCountryCode]);

  // Carga de referidos (Pestaña 1)
  const loadReferrals = useCallback(
    async (page: number = 1) => {
      try {
        const params = {
          search: search || undefined,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          status: selectedStatus || undefined,
          line: selectedLine || undefined,
          assigned_responsible_id: selectedResponsibleId || undefined,
          page,
          limit: 10,
        };

        const { status, data } = await callEndpoint(
          getAdminReferralListEndpoint(params, token),
        );

        if (status === 200) {
          const response = fetchResponseAdapter(data);
          const responseData = response.data || {};
          setItems(responseData.items || []);
          setKpis(responseData.kpis || DEFAULT_KPIS);
          setMeta(responseData.meta || DEFAULT_META);
          setCurrentPage(page);
        }
      } catch (error) {
        console.error('Error loading referrals:', error);
      }
    },
    [callEndpoint, token, search, startDate, endDate, selectedStatus, selectedLine, selectedResponsibleId],
  );

  // Carga de pagos (Pestaña 2)
  const loadPayments = useCallback(
    async (page: number = 1) => {
      try {
        const params = {
          search: paymentSearch || undefined,
          start_date: paymentStartDate || undefined,
          end_date: paymentEndDate || undefined,
          payment_status: paymentSelectedStatus || undefined,
          category: paymentSelectedCategory || undefined,
          page,
          limit: 10,
        };

        const { status, data } = await callEndpoint(
          getAdminPaymentListEndpoint(params, token),
        );

        if (status === 200) {
          const response = fetchResponseAdapter(data);
          const responseData = response.data || {};
          setPaymentItems(responseData.items || []);
          setPaymentKpis(responseData.kpis || DEFAULT_PAYMENT_KPIS);
          setPaymentMeta(responseData.meta || DEFAULT_META);
          setPaymentCurrentPage(page);
        }
      } catch (error) {
        console.error('Error loading payments:', error);
      }
    },
    [callEndpoint, token, paymentSearch, paymentStartDate, paymentEndDate, paymentSelectedStatus, paymentSelectedCategory],
  );

  // Inicialización
  useEffect(() => {
    loadResponsibles();
  }, [loadResponsibles]);

  // Recarga al cambiar filtros de referidos
  useEffect(() => {
    if (activeTab === 'referrals') {
      loadReferrals(1);
    }
  }, [activeTab, search, startDate, endDate, selectedStatus, selectedLine, selectedResponsibleId]);

  // Recarga al cambiar filtros de pagos
  useEffect(() => {
    if (activeTab === 'payments') {
      loadPayments(1);
    }
  }, [activeTab, paymentSearch, paymentStartDate, paymentEndDate, paymentSelectedStatus, paymentSelectedCategory]);

  // Limpiar filtros de referidos
  const handleClearFilters = () => {
    setSearch('');
    setStartDate('');
    setEndDate('');
    setSelectedStatus('');
    setSelectedLine('');
    setSelectedResponsibleId('');
  };

  // Limpiar filtros de pagos
  const handleClearPaymentFilters = () => {
    setPaymentSearch('');
    setPaymentStartDate('');
    setPaymentEndDate('');
    setPaymentSelectedStatus('');
    setPaymentSelectedCategory('');
  };

  // Navegación al detalle del referidor (Vista 2)
  const handleViewDetail = (referrerProfileId: string) => {
    if (referrerProfileId) {
      navigate(`/referrals/${referrerProfileId}`);
    }
  };

  // Selección de checkboxes en pagos
  const handleToggleSelectPayment = (id: string) => {
    setSelectedPaymentIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  // Seleccionar todos los incompletos
  const handleSelectAllIncomplete = () => {
    const incompleteIds = paymentItems
      .filter((i) => i.payment_status === 'incomplete_data')
      .map((i) => i.id);
    setSelectedPaymentIds(incompleteIds);
  };

  // Deseleccionar todos
  const handleClearPaymentSelection = () => {
    setSelectedPaymentIds([]);
  };

  // Enviar recordatorios en lote (a seleccionados o a todos los incompletos)
  const handleSendBatchReminders = async () => {
    const targetIds =
      selectedPaymentIds.length > 0
        ? selectedPaymentIds
        : paymentItems
          .filter((i) => i.payment_status === 'incomplete_data')
          .map((i) => i.id);

    if (targetIds.length === 0) return;

    try {
      setIsSendingBatchReminders(true);
      const results = await Promise.allSettled(
        targetIds.map((id) =>
          callEndpoint(sendReferralReminderEndpoint(id, token)),
        ),
      );

      const isSuccessStatus = (status?: number) =>
        typeof status === 'number' && status >= 200 && status < 300;

      const successCount = results.filter(
        (r) =>
          r.status === 'fulfilled' && isSuccessStatus((r.value as any)?.status),
      ).length;

      const failCount = targetIds.length - successCount;

      setSelectedPaymentIds([]);

      if (successCount > 0) {
        setAlertOptions({
          success: true,
          title: 'Recordatorios enviados',
          text:
            failCount > 0
              ? `Se enviaron ${successCount} correos de recordatorio exitosamente. ${failCount} no pudieron ser enviados.`
              : `Se enviaron ${successCount} correos de recordatorio exitosamente a los referidores.`,
          onConfirm: () => setAlertOptions(null),
          onCancel: () => setAlertOptions(null),
        });
      } else {
        setAlertOptions({
          danger: true,
          title: 'Error al enviar recordatorios',
          text: 'No se pudo enviar ningún recordatorio. Verifica que los referidores tengan un correo electrónico válido registrado.',
          onConfirm: () => setAlertOptions(null),
          onCancel: () => setAlertOptions(null),
        });
      }
    } catch (error) {
      console.error('Error sending batch reminders:', error);
      setAlertOptions({
        danger: true,
        title: 'Error al enviar recordatorios',
        text: 'Ocurrió un error al intentar enviar algunos recordatorios.',
        onConfirm: () => setAlertOptions(null),
        onCancel: () => setAlertOptions(null),
      });
    } finally {
      setIsSendingBatchReminders(false);
    }
  };

  // Enviar recordatorio al referidor
  const handleSendReminder = async (referralId: string) => {
    try {
      setIsSendingReminderId(referralId);
      const { status } = await callEndpoint(
        sendReferralReminderEndpoint(referralId, token),
      );
      if (status === 200 || status === 201) {
        setAlertOptions({
          success: true,
          title: 'Recordatorio enviado',
          text: 'Se envió un correo de recordatorio al referidor exitosamente.',
          onConfirm: () => setAlertOptions(null),
          onCancel: () => setAlertOptions(null),
        });
      } else {
        setAlertOptions({
          danger: true,
          title: 'Error al enviar recordatorio',
          text: 'No se pudo enviar el correo de recordatorio. Por favor verifica que el referidor tenga un correo válido.',
          onConfirm: () => setAlertOptions(null),
          onCancel: () => setAlertOptions(null),
        });
      }
    } catch (error) {
      console.error('Error sending reminder:', error);
      setAlertOptions({
        danger: true,
        title: 'Error al enviar recordatorio',
        text: 'Ocurrió un error al intentar enviar el recordatorio.',
        onConfirm: () => setAlertOptions(null),
        onCancel: () => setAlertOptions(null),
      });
    } finally {
      setIsSendingReminderId(null);
    }
  };

  // Registrar al prospecto referido como usuario
  const handleRegisterProspect = async (item: ReferralItem) => {
    try {
      setRegisteringProspectId(item.id);
      const { status, data } = await callEndpoint(
        registerReferralProspectEndpoint(item.id, token),
      );
      if (status === 200 || status === 201) {
        const response = fetchResponseAdapter(data);
        const updatedReferral = response.data;
        const newProfileId = updatedReferral?.referred_profile_id;

        // Actualizar el estado local para reflejar que ya tiene cuenta inmediatamente
        setItems((prevItems) =>
          prevItems.map((ref) =>
            ref.id === item.id
              ? {
                ...ref,
                referred_profile_id: newProfileId || 'registered',
                referredProfile: updatedReferral?.referredProfile || ref.referredProfile,
              }
              : ref,
          ),
        );

        const fullName = `${item.referred_first_name} ${item.referred_last_name}`.trim();
        setAlertOptions({
          success: true,
          title: 'Cuenta creada con éxito',
          text: `Se ha registrado y vinculado exitosamente la cuenta de usuario para ${fullName}.`,
          onConfirm: () => setAlertOptions(null),
          onCancel: () => setAlertOptions(null),
        });
      } else {
        setAlertOptions({
          danger: true,
          title: 'Error al crear cuenta',
          text: data?.message || 'No se pudo registrar la cuenta para el prospecto.',
          onConfirm: () => setAlertOptions(null),
          onCancel: () => setAlertOptions(null),
        });
      }
    } catch (error: any) {
      console.error('Error registering prospect:', error);
      setAlertOptions({
        danger: true,
        title: 'Error al crear cuenta',
        text: error?.response?.data?.message || 'Ocurrió un problema al intentar crear la cuenta.',
        onConfirm: () => setAlertOptions(null),
        onCancel: () => setAlertOptions(null),
      });
    } finally {
      setRegisteringProspectId(null);
    }
  };

  // Abrir modal de marcar pagado
  const handleOpenMarkPaidModal = (item: PaymentDashboardItem) => {
    setMarkPaidItem(item);
    setIsMarkPaidModalOpen(true);
  };

  // Confirmar marcar como pagado
  const handleConfirmMarkPaid = async (
    referralId: string,
    paymentReference: string,
  ) => {
    try {
      setIsMarkingPaid(true);
      const { status } = await callEndpoint(
        markReferralPaidEndpoint(
          referralId,
          { payment_reference: paymentReference },
          token,
        ),
      );
      if (status === 200) {
        setAlertOptions({
          success: true,
          title: 'Pago liquidado',
          text: 'El pago ha sido registrado como liquidado exitosamente.',
          onConfirm: () => setAlertOptions(null),
          onCancel: () => setAlertOptions(null),
        });
        await loadPayments(paymentCurrentPage);
      }
    } catch (error) {
      console.error('Error marking paid:', error);
    } finally {
      setIsMarkingPaid(false);
    }
  };

  return (
    <AdminPage
      name="Plan de Referidos"
      parentName="Proyectos"
      globalAlertOptions={alertOptions || undefined}
    >
      {/* Modal para marcar como pagado */}
      <MarkPaidModal
        isOpen={isMarkPaidModalOpen}
        toggle={() => setIsMarkPaidModalOpen(false)}
        item={markPaidItem}
        onConfirm={handleConfirmMarkPaid}
        isLoading={isMarkingPaid}
      />

      {/* Tabs de Navegación */}
      <div className="d-flex align-items-center mb-4">
        <div
          className="p-1 d-inline-flex align-items-center"
          style={{
            backgroundColor: '#f1f5f9',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
          }}
        >
          <button
            type="button"
            className="btn btn-sm font-weight-600"
            style={{
              borderRadius: '6px',
              padding: '7px 22px',
              fontSize: '13px',
              border: 'none',
              backgroundColor: activeTab === 'referrals' ? '#00c389' : 'transparent',
              color: activeTab === 'referrals' ? '#ffffff' : '#64748b',
              boxShadow:
                activeTab === 'referrals'
                  ? '0 2px 6px rgba(0, 195, 137, 0.3)'
                  : 'none',
              transition: 'all 0.15s ease',
            }}
            onClick={() => handleTabChange('referrals')}
          >
            <i className="fas fa-users mr-2" />
            Referidos
          </button>

          <button
            type="button"
            className="btn btn-sm font-weight-600"
            style={{
              borderRadius: '6px',
              padding: '7px 22px',
              fontSize: '13px',
              border: 'none',
              backgroundColor: activeTab === 'payments' ? '#00c389' : 'transparent',
              color: activeTab === 'payments' ? '#ffffff' : '#64748b',
              boxShadow:
                activeTab === 'payments'
                  ? '0 2px 6px rgba(0, 195, 137, 0.3)'
                  : 'none',
              transition: 'all 0.15s ease',
            }}
            onClick={() => handleTabChange('payments')}
          >
            <i className="fas fa-credit-card mr-2" />
            Dashboard Pagos
          </button>
        </div>
      </div>

      {/* Contenido del Tab 1: Referidos */}
      {activeTab === 'referrals' && (
        <>
          {/* Tarjetas de KPIs */}
          <ReferralKpiCards kpis={kpis} />

          {/* Barra de Filtros */}
          <ReferralFilterBar
            search={search}
            startDate={startDate}
            endDate={endDate}
            selectedStatus={selectedStatus}
            selectedLine={selectedLine}
            selectedResponsibleId={selectedResponsibleId}
            responsibleOptions={responsibleOptions}
            isLoadingResponsibles={isLoadingResponsibles}
            onSearchChange={setSearch}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onStatusChange={setSelectedStatus}
            onLineChange={setSelectedLine}
            onResponsibleChange={setSelectedResponsibleId}
            onClearFilters={handleClearFilters}
          />

          {/* Tabla de Referidos */}
          <ReferralListTable
            items={items}
            isLoading={isLoading}
            onViewDetail={handleViewDetail}
            onRegisterProspect={handleRegisterProspect}
            registeringId={registeringProspectId}
            paginationComponent={
              meta.pageCount > 1 ? (
                <AdminPaginator
                  page={currentPage}
                  totalPages={meta.pageCount}
                  onPageChange={(page) => loadReferrals(page)}
                />
              ) : null
            }
          />
        </>
      )}

      {/* Contenido del Tab 2: Dashboard Pagos */}
      {activeTab === 'payments' && (
        <>
          {/* Tarjetas de KPIs de Pagos */}
          <PaymentKpiCards kpis={paymentKpis} />

          {/* Barra de Filtros de Pagos */}
          <PaymentFilterBar
            search={paymentSearch}
            startDate={paymentStartDate}
            endDate={paymentEndDate}
            selectedStatus={paymentSelectedStatus}
            selectedCategory={paymentSelectedCategory}
            onSearchChange={setPaymentSearch}
            onStartDateChange={setPaymentStartDate}
            onEndDateChange={setPaymentEndDate}
            onStatusChange={setPaymentSelectedStatus}
            onCategoryChange={setPaymentSelectedCategory}
            onClearFilters={handleClearPaymentFilters}
          />

          {/* Tabla del Dashboard de Pagos */}
          <PaymentListTable
            items={paymentItems}
            isLoading={isLoading}
            selectedIds={selectedPaymentIds}
            onToggleSelect={handleToggleSelectPayment}
            onSelectAllIncomplete={handleSelectAllIncomplete}
            onClearSelection={handleClearPaymentSelection}
            onSendBatchReminders={handleSendBatchReminders}
            isSendingBatchReminders={isSendingBatchReminders}
            onMarkPaidClick={handleOpenMarkPaidModal}
            onSendReminder={handleSendReminder}
            isSendingReminderId={isSendingReminderId}
            paginationComponent={
              paymentMeta.pageCount > 1 ? (
                <AdminPaginator
                  page={paymentCurrentPage}
                  totalPages={paymentMeta.pageCount}
                  onPageChange={(page) => loadPayments(page)}
                />
              ) : null
            }
          />
        </>
      )}
    </AdminPage>
  );
};

export default InvestorReferralPlans;